import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const LinkedWorkOrders = ({ workOrderId, onViewDetails }) => {
  const [linkedWOs, setLinkedWOs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (workOrderId) {
      fetchLinkedWOs();
    }
  }, [workOrderId]);

  const fetchLinkedWOs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/api/maintenance/${workOrderId}/linked-wos`);
      setLinkedWOs(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch linked work orders';
      setError(errorMessage);
      console.error('Error fetching linked work orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SCHEDULED': return 'warning';
      case 'HOLD': return 'default';
      case 'IN_PROGRESS': return 'info';
      case 'PARTS_PROCURED': return 'info';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'LOW': return 'success';
      case 'MEDIUM': return 'warning';
      case 'HIGH': return 'error';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (linkedWOs.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No linked child work orders
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Linked child work orders
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>WO #</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linkedWOs.map((wo) => (
              <TableRow key={wo._id}>
                <TableCell>{wo.workOrderNumber || '—'}</TableCell>
                <TableCell>
                  {wo.isEdrWorkOrder ? (
                    <Chip label="EDR" size="small" color="secondary" variant="outlined" />
                  ) : wo.isPartsProcurementWO ? (
                    <Chip label="Parts" size="small" variant="outlined" />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{wo.title}</Typography>
                  {wo.description && (
                    <Typography variant="caption" color="text.secondary">
                      {wo.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={wo.priority}
                    color={getPriorityColor(wo.priority)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={wo.status}
                    color={getStatusColor(wo.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {wo.createdAt ? new Date(wo.createdAt).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => onViewDetails && onViewDetails(wo)}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LinkedWorkOrders;

